import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import {
  Trophy, TrendingUp, TrendingDown, Minus, Users, RefreshCw,
  ChevronRight, ChevronLeft, Target, Award, Zap, Star, BarChart3,
  ArrowUpRight, ArrowDownRight, Lightbulb, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, Radar, PieChart, Pie, Cell,
  LineChart, Line
} from "recharts";
import { toast } from "sonner";
import StaffPulseSection from "../components/StaffPulseSection";

const METRIC_LABELS = {
  avg_ticket: "Avg Ticket",
  rebooking: "Rebooking",
  upsell: "Upsell",
  retail: "Retail",
  retention: "Retention",
  new_client: "New Client",
  utilization: "Utilization",
  feedback: "Feedback",
  attendance: "Attendance",
  goals: "Goals",
};

function ScoreRing({ score, size = 64, color, label }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E6E4E0" strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-700" />
      </svg>
      <span className="absolute font-['Outfit'] text-lg font-semibold" style={{ color }}>{score}</span>
      {label && <span className="text-[10px] text-slate-400 mt-0.5">{label}</span>}
    </div>
  );
}

function TrendBadge({ trend, delta }) {
  if (trend === "up") return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-600 bg-[#E8F0E9] px-1.5 py-0.5 rounded-full">
      <TrendingUp className="w-3 h-3" />+{delta}
    </span>
  );
  if (trend === "down") return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-full">
      <TrendingDown className="w-3 h-3" />{delta}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-slate-400 bg-[#F3F3F3] px-1.5 py-0.5 rounded-full">
      <Minus className="w-3 h-3" />0
    </span>
  );
}

function MetricBadge({ score }) {
  const bg = score >= 75 ? "#E8F0E9" : score >= 50 ? "#FDF3E1" : "#FBEAE9";
  const fg = score >= 75 ? "#2D5936" : score >= 50 ? "#805B10" : "#8A2A23";
  return (
    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold" style={{ backgroundColor: bg, color: fg }}>
      {score}
    </div>
  );
}

function StaffScoreCard({ member, onSelect }) {
  return (
    <div
      data-testid={`team-score-card-${member.user_id}`}
      onClick={() => onSelect(member)}
      className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-rose-300 transition-all duration-200 cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
            style={{ backgroundColor: member.color }}>
            {member.name?.split(" ").map(n => n[0]).join("")}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{member.name}</p>
            <p className="text-xs text-slate-400">{member.title}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1.5">
            <span className="font-['Outfit'] text-2xl font-semibold" style={{ color: member.tier_color }}>{member.score}</span>
          </div>
          <TrendBadge trend={member.trend} delta={member.score_delta} />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs px-2.5 py-1 rounded-full font-medium"
          style={{ backgroundColor: `${member.tier_color}15`, color: member.tier_color }}>
          {member.tier}
        </span>
        <span className="text-[11px] text-slate-400 capitalize">{member.role?.replace("_", " ")}</span>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {["avg_ticket", "rebooking", "retail", "retention", "feedback"].map(k => (
          <div key={k} className="text-center">
            <MetricBadge score={member.breakdown?.[k]?.score || 0} />
            <p className="text-[9px] text-slate-400 mt-1 truncate">{METRIC_LABELS[k]}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end text-xs text-rose-400 group-hover:text-slate-900 transition-colors">
        View details <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
      </div>
    </div>
  );
}

function DetailPanel({ member, onClose }) {
  if (!member) return null;
  const bd = member.breakdown || {};
  const radarData = Object.entries(bd).map(([k, v]) => ({
    metric: METRIC_LABELS[k] || k,
    score: v.score,
    fullMark: 100,
  }));
  const history = (member.history || []).map((h, i) => ({
    label: `M${i + 1}`,
    score: h.score,
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/30 backdrop-blur-sm" data-testid="score-detail-panel">
      <div className="w-full max-w-[560px] h-full bg-white overflow-y-auto animate-slideInRight shadow-2xl">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold text-white"
              style={{ backgroundColor: member.color }}>
              {member.name?.split(" ").map(n => n[0]).join("")}
            </div>
            <div>
              <h2 className="font-['Outfit'] text-xl font-medium text-slate-900">{member.name}</h2>
              <p className="text-xs text-slate-400">{member.title}</p>
            </div>
          </div>
          <button data-testid="close-detail-panel" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F3F3] flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Score + Tier */}
          <div className="flex items-center gap-6">
            <div className="relative flex items-center justify-center">
              <ScoreRing score={member.score} size={80} color={member.tier_color} />
            </div>
            <div>
              <span className="text-xs px-3 py-1 rounded-full font-medium"
                style={{ backgroundColor: `${member.tier_color}15`, color: member.tier_color }}>
                {member.tier}
              </span>
              <p className="text-sm text-slate-600 mt-2">{member.explanation}</p>
              <div className="mt-1"><TrendBadge trend={member.trend} delta={member.score_delta} /></div>
            </div>
          </div>

          {/* Radar Chart */}
          <div className="bg-[#FAFAFA] border border-slate-200 rounded-2xl p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Performance Radar</h3>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={radarData} outerRadius={90}>
                <PolarGrid stroke="#E6E4E0" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: "#5E5E5E" }} />
                <Radar dataKey="score" stroke={member.tier_color} fill={member.tier_color} fillOpacity={0.15} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Score Breakdown */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Metric Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(bd).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 w-20 truncate">{METRIC_LABELS[k]}</span>
                  <div className="flex-1 h-2 bg-[#F3F3F3] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${v.score}%`,
                        backgroundColor: v.score >= 75 ? "#2D5936" : v.score >= 50 ? "#805B10" : "#8A2A23"
                      }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-900 w-8 text-right">{v.score}</span>
                  <span className="text-[10px] text-slate-400 w-14 text-right">{v.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Score History */}
          {history.length > 1 && (
            <div className="bg-[#FAFAFA] border border-slate-200 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Score Trend</h3>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={history}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8A8A8A" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip formatter={(v) => [v, "Score"]} />
                  <Line type="monotone" dataKey="score" stroke={member.tier_color} strokeWidth={2} dot={{ r: 3, fill: member.tier_color }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Key Metrics */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Key Metrics</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Total Appointments", value: member.metrics?.total_appointments },
                { label: "Avg Ticket", value: `$${member.metrics?.avg_ticket}` },
                { label: "Rebook Rate", value: `${member.metrics?.rebook_rate}%` },
                { label: "Retail Attach", value: `${member.metrics?.retail_attach}%` },
                { label: "Utilization", value: `${member.metrics?.util_rate}%` },
                { label: "Review Count", value: member.metrics?.review_count },
                { label: "Unique Clients", value: member.metrics?.unique_clients },
                { label: "Hours Worked", value: member.metrics?.hours_worked },
              ].map(m => (
                <div key={m.label} className="bg-white border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-400">{m.label}</p>
                  <p className="font-['Outfit'] text-lg font-medium text-slate-900">{m.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Insights */}
          {(member.insights || []).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Insights</h3>
              <div className="space-y-2">
                {member.insights.map((ins, i) => (
                  <div key={`${member.user_id}-ins-${i}`} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${
                    ins.type === "strength" ? "bg-[#E8F0E9] text-emerald-600" : "bg-[#FDF3E1] text-amber-600"
                  }`}>
                    {ins.type === "strength" ? <ArrowUpRight className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                    {ins.text}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {(member.actions || []).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Coaching Actions</h3>
              <div className="space-y-2">
                {member.actions.map((a, i) => (
                  <div key={`${member.user_id}-act-${i}`} className="flex items-start gap-2 px-3 py-2.5 bg-[#FAFAFA] rounded-lg text-sm text-slate-900">
                    <Target className="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-400" />
                    {a}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TeamPerformancePage() {
  const [dashboard, setDashboard] = useState(null);
  const [allScores, setAllScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sortBy, setSortBy] = useState("score");

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, scoresRes] = await Promise.all([
        api.get("/team-scores-dashboard"),
        api.get("/team-scores"),
      ]);
      setDashboard(dashRes.data);
      setAllScores(scoresRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await api.post("/team-scores/refresh-all");
      await fetchData();
      toast.success("All team scores refreshed");
    } catch (err) { toast.error("Failed to refresh"); }
    finally { setRefreshing(false); }
  };

  const openDetail = async (member) => {
    try {
      const res = await api.get(`/team-scores/${member.user_id}`);
      setSelected(res.data);
    } catch {
      setSelected(member);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const d = dashboard || {};
  const sorted = [...allScores].sort((a, b) => {
    if (sortBy === "score") return b.score - a.score;
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "trend") return (b.score_delta || 0) - (a.score_delta || 0);
    return b.score - a.score;
  });

  const tierData = (d.tiers || []).filter(t => t.count > 0);
  const TIER_COLORS = {
    "Elite Performer": "#1A1A1A",
    "Strong Performer": "#8B5CF6",
    "Solid Contributor": "#2D5936",
    "Needs Coaching": "#805B10",
    "At Risk": "#8A2A23",
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="team-performance-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Team Performance</h1>
          <p className="text-slate-600 text-sm mt-1">Performance intelligence and coaching insights for your team</p>
        </div>
        <Button data-testid="refresh-team-scores-btn" onClick={refreshAll} disabled={refreshing} variant="outline"
          className="border-slate-200 rounded-full h-10 px-5 text-sm">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? "Calculating..." : "Recalculate All"}
        </Button>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><Trophy className="w-5 h-5 text-rose-400" strokeWidth={1.5} /></div>
          <p data-testid="team-avg-score" className="font-['Outfit'] text-4xl font-medium text-slate-900">{d.avg_score || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Team Average</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-slate-400" strokeWidth={1.5} /></div>
          <p data-testid="team-total-staff" className="font-['Outfit'] text-4xl font-medium text-slate-900">{d.total || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Team Members</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><Award className="w-5 h-5 text-[#8B5CF6]" strokeWidth={1.5} /></div>
          <p data-testid="team-top-performers" className="font-['Outfit'] text-4xl font-medium text-[#8B5CF6]">
            {(d.tiers || []).filter(t => t.name === "Elite Performer" || t.name === "Strong Performer").reduce((s, t) => s + t.count, 0)}
          </p>
          <p className="text-sm text-slate-400 mt-1">Top Performers</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><Zap className="w-5 h-5 text-amber-600" strokeWidth={1.5} /></div>
          <p data-testid="team-needs-coaching" className="font-['Outfit'] text-4xl font-medium text-amber-600">
            {(d.coaching || []).length}
          </p>
          <p className="text-sm text-slate-400 mt-1">Needs Coaching</p>
        </div>
      </div>

      {/* Staff Pulse */}
      <StaffPulseSection />

      {/* Charts + Leaderboard Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Tier Distribution */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="team-tier-distribution">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Tier Distribution</h3>
          <div className="flex gap-4">
            <div className="w-1/2 h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={tierData} dataKey="count" nameKey="name"
                    cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3}>
                    {tierData.map((t) => (
                      <Cell key={t.name} fill={TIER_COLORS[t.name] || t.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2.5 justify-center flex flex-col">
              {(d.tiers || []).map(t => (
                <div key={t.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TIER_COLORS[t.name] || t.color }} />
                    <span className="text-xs text-slate-600">{t.name}</span>
                  </div>
                  <span className="text-xs font-semibold text-slate-900">{t.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Score Leaderboard */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="team-leaderboard">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Leaderboard</h3>
          <div className="space-y-2">
            {(d.top || []).slice(0, 8).map((m, i) => (
              <div key={m.user_id} className="flex items-center justify-between py-1.5 cursor-pointer hover:bg-[#FAFAFA] -mx-2 px-2 rounded-lg transition-colors"
                data-testid={`leaderboard-row-${m.user_id}`}
                onClick={() => openDetail(m)}>
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ backgroundColor: m.tier_color || "#8A8A8A" }}>
                    {i + 1}
                  </span>
                  <div>
                    <span className="text-sm font-medium text-slate-900">{m.name}</span>
                    <span className="text-xs text-slate-400 ml-2 capitalize">{m.role?.replace("_", " ")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-['Outfit'] text-base font-semibold" style={{ color: m.tier_color }}>{m.score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Leaders */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="category-leaders">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900 mb-4">Category Leaders</h3>
          <div className="space-y-4">
            {Object.entries(d.leaderboards || {}).slice(0, 5).map(([cat, entries]) => (
              <div key={cat}>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">{METRIC_LABELS[cat] || cat}</p>
                {(entries || []).slice(0, 3).map((e, i) => (
                  <div key={`${cat}-${e.user_id || e.name || i}`} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-rose-400 w-4">{i + 1}.</span>
                      <span className="text-xs text-slate-900">{e.name}</span>
                    </div>
                    <span className="text-xs font-semibold text-slate-600">{e.value || e.score}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Coaching Queue */}
      {(d.coaching || []).length > 0 && (
        <div className="bg-[#FDF3E1] border border-[#F3D8A2] rounded-2xl p-6 mb-8" data-testid="coaching-queue">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-amber-600" />
            <h3 className="font-['Outfit'] text-base font-medium text-amber-600">Coaching Queue</h3>
          </div>
          <p className="text-sm text-amber-600/70 mb-4">Team members scoring below 60 who need focused coaching and support</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {d.coaching.map(m => (
              <div key={m.user_id} className="bg-white/60 rounded-lg p-4 cursor-pointer hover:bg-white/80 transition-colors"
                onClick={() => openDetail(m)}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-amber-600">{m.name}</span>
                  <span className="font-['Outfit'] text-lg font-semibold text-amber-600">{m.score}</span>
                </div>
                {(m.actions || []).map((a, i) => (
                  <p key={`${m.user_id}-coach-${i}`} className="text-xs text-amber-600/70 flex items-start gap-1.5 mt-1">
                    <Lightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" />{a}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sort Controls */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Sort by</span>
        {[
          { key: "score", label: "Score" },
          { key: "name", label: "Name" },
          { key: "trend", label: "Trend" },
        ].map(opt => (
          <button key={opt.key} data-testid={`sort-${opt.key}`}
            onClick={() => setSortBy(opt.key)}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
              sortBy === opt.key ? "bg-slate-900 text-white" : "bg-[#F3F3F3] text-slate-600 hover:bg-[#E6E4E0]"
            }`}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Team Score Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="team-scores-grid">
        {sorted.map(m => (
          <StaffScoreCard key={m.user_id} member={m} onSelect={openDetail} />
        ))}
      </div>

      {/* Empty State */}
      {allScores.length === 0 && (
        <div className="text-center py-20">
          <Trophy className="w-12 h-12 text-[#E6E4E0] mx-auto mb-4" />
          <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-2">No Team Scores Yet</h3>
          <p className="text-sm text-slate-400 mb-4">Click "Recalculate All" to generate performance scores for your team</p>
          <Button onClick={refreshAll} disabled={refreshing} variant="outline"
            className="border-slate-200 rounded-full h-10 px-5 text-sm">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Calculate Scores
          </Button>
        </div>
      )}

      {/* Detail Panel */}
      {selected && <DetailPanel member={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
